import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import useLocationSearch, { fetchPropertyLocations } from "../hooks/useLocationSearch";
import DateRangePicker from "./DateRangePicker";
import GuestSelector from "./GuestSelector";
import { resolveLocationSearchValue } from "../utils/searchLocationValue";
import "./SearchBar.css";

const TABS = [
  { key: "stays", label: "Stays", shortLabel: "Stays" },
  { key: "packages", label: "Packages", shortLabel: "Packages" },
  { key: "cabs", label: "Cab Service", shortLabel: "Cabs" },
];

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const pad2 = (value) => String(value).padStart(2, "0");
const toIso = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const formatFieldDate = (date) => {
  if (!date) return "Select date";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const mergeLocationSuggestions = (primary = [], fallback = []) =>
  Array.from(
    [...primary, ...fallback]
      .filter(Boolean)
      .reduce((map, item) => {
        const key = [
          item?.type || "city",
          String(item?.display_name || item?.city || "").trim().toLowerCase(),
          String(item?.state || "").trim().toLowerCase(),
          String(item?.country || "").trim().toLowerCase(),
        ].join("|");
        if (!key.replace(/\|/g, "")) return map;
        const existing = map.get(key);
        if (existing) {
          map.set(key, {
            ...existing,
            property_count: Math.max(Number(existing.property_count || 0), Number(item.property_count || 0)),
          });
          return map;
        }
        map.set(key, item);
        return map;
      }, new Map())
      .values()
  );

const LocationInput = memo(function LocationInput({
  label,
  placeholder,
  inputId,
  value,
  open,
  loading,
  suggestions,
  isPopular,
  error,
  onChange,
  onSelect,
  onFocus,
  onBlur,
}) {
  return (
    <div className="hv-field hv-location-field">
      <label htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete="off"
      />

      <div className={`hv-location-menu ${open ? "is-open" : ""}`}>
        {loading ? <div className="hv-location-empty">Loading...</div> : null}

        {!loading && suggestions.length === 0 ? (
          <div className="hv-location-empty">No locations found.</div>
        ) : null}

        {!loading && suggestions.length > 0 ? (
          <>
            <div className="hv-location-head">
              {isPopular ? "Popular destinations" : "Search results"}
            </div>
            <ul>
              {suggestions.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onSelect(item)}
                  >
                    <strong>{item.display_name || item.city}</strong>
                    <span>
                      {item.property_count
                        ? `${item.property_count} stays`
                        : item.country || "Destination"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {error ? <div className="hv-location-error">{error}</div> : null}
      </div>
    </div>
  );
});

export default function SearchBar({ variant = "hero", defaultValues = null }) {
  const navigate = useNavigate();
  const isCompact = variant === "compact";
  const showTabs = !isCompact;

  const [activeTab, setActiveTab] = useState("stays");
  const [locationInput, setLocationInput] = useState(defaultValues?.city || "");
  const [selectedLocation, setSelectedLocation] = useState(defaultValues?.location || null);
  const [checkIn, setCheckIn] = useState(() => toDate(defaultValues?.checkIn));
  const [checkOut, setCheckOut] = useState(() => toDate(defaultValues?.checkOut));
  const [guests, setGuests] = useState({
    adults: Number(defaultValues?.adults || 2),
    children: Number(defaultValues?.children || 0),
  });

  const [openMenu, setOpenMenu] = useState(false);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [openGuests, setOpenGuests] = useState(false);
  const [calendarAnchorRect, setCalendarAnchorRect] = useState(null);
  const [guestAnchorRect, setGuestAnchorRect] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [propertyLocationSuggestions, setPropertyLocationSuggestions] = useState([]);
  const [propertyLocationsLoading, setPropertyLocationsLoading] = useState(false);

  const blurTimerRef = useRef(null);

  const { suggestions, loading, error, isPopular } = useLocationSearch(locationInput);
  const mergedSuggestions = useMemo(
    () => mergeLocationSuggestions(suggestions, propertyLocationSuggestions),
    [suggestions, propertyLocationSuggestions]
  );
  const mergedLoading = loading || propertyLocationsLoading;

  useEffect(() => {
    const query = String(locationInput || "").trim();
    if (activeTab !== "stays" || query.length < 2) {
      setPropertyLocationSuggestions([]);
      setPropertyLocationsLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setPropertyLocationsLoading(true);
      try {
        const items = await fetchPropertyLocations(query, controller.signal, 160);
        if (!controller.signal.aborted) setPropertyLocationSuggestions(items);
      } catch (_) {
        if (!controller.signal.aborted) setPropertyLocationSuggestions([]);
      } finally {
        if (!controller.signal.aborted) setPropertyLocationsLoading(false);
      }
    }, 180);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [activeTab, locationInput]);

  useEffect(() => {
    if (!defaultValues) return;
    setLocationInput(defaultValues.city || "");
    setSelectedLocation(defaultValues.location || null);
    setCheckIn(toDate(defaultValues.checkIn));
    setCheckOut(toDate(defaultValues.checkOut));
    setGuests({
      adults: Number(defaultValues.adults || 2),
      children: Number(defaultValues.children || 0),
    });
  }, [defaultValues]);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) {
        window.clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  const handleLocationChange = useCallback((event) => {
    const next = event.target.value;
    setLocationInput(next);
    setSelectedLocation(null);
    setOpenMenu(true);
  }, []);

  const handleLocationSelect = useCallback((location) => {
    setSelectedLocation(location);
    setLocationInput(location.display_name || "");
    setOpenMenu(false);
  }, []);

  const handleLocationFocus = useCallback(() => {
    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setOpenMenu(true);
    setOpenCalendar(false);
    setOpenGuests(false);
  }, []);

  const handleLocationBlur = useCallback(() => {
    blurTimerRef.current = window.setTimeout(() => setOpenMenu(false), 120);
  }, []);

  const closePanels = useCallback(() => {
    setOpenMenu(false);
    setOpenCalendar(false);
    setOpenGuests(false);
  }, []);

  useEffect(() => {
    if (!isMobileSearchOpen || typeof document === "undefined") return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKey = (event) => {
      if (event.key === "Escape") {
        if (openCalendar || openGuests || openMenu) {
          closePanels();
          return;
        }
        setIsMobileSearchOpen(false);
        closePanels();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKey);
    };
  }, [isMobileSearchOpen, openCalendar, openGuests, openMenu, closePanels]);

  const handleSearch = useCallback(async () => {
    const params = new URLSearchParams();
    const trimmedLocation = resolveLocationSearchValue(selectedLocation, locationInput);
    const totalGuests = guests.adults + guests.children;
    const checkInIso = toIso(checkIn);
    const checkOutIso = toIso(checkOut);

    if (selectedLocation?.id && selectedLocation.type !== "state") {
      params.set("locationId", String(selectedLocation.id));
    }
    if (trimmedLocation) {
      params.set(activeTab === "packages" ? "destination" : "city", trimmedLocation);
    }
    if (checkInIso) {
      params.set(activeTab === "cabs" ? "pickupDate" : "checkIn", checkInIso);
    }
    if (checkOutIso && activeTab !== "cabs") {
      params.set("checkOut", checkOutIso);
    }

    if (activeTab === "packages") {
      params.set("travellers", String(Math.max(1, totalGuests)));
    } else if (activeTab === "cabs") {
      params.set("passengers", String(Math.max(1, totalGuests)));
      if (checkOutIso) params.set("returnDate", checkOutIso);
    } else {
      params.set("adults", String(guests.adults));
      params.set("children", String(guests.children));
    }

    setIsSearching(true);
    await new Promise((resolve) => window.setTimeout(resolve, 180));

    if (activeTab === "packages") {
      navigate(`/packages${params.toString() ? `?${params.toString()}` : ""}`);
    } else if (activeTab === "cabs") {
      navigate(`/cabs/list${params.toString() ? `?${params.toString()}` : ""}`);
    } else {
      navigate(`/listings${params.toString() ? `?${params.toString()}` : ""}`);
    }

    setIsSearching(false);
    setIsMobileSearchOpen(false);
    closePanels();
  }, [activeTab, selectedLocation, locationInput, checkIn, checkOut, guests, navigate, closePanels]);

  const guestLabel = useMemo(() => {
    const total = guests.adults + guests.children;
    if (activeTab === "cabs") return `${total} Passenger${total === 1 ? "" : "s"}`;
    if (activeTab === "packages") return `${total} Traveller${total === 1 ? "" : "s"}`;
    return `${total} Guest${total === 1 ? "" : "s"}`;
  }, [guests, activeTab]);

  const locationLabel = activeTab === "packages" ? "Destination" : activeTab === "cabs" ? "Pickup" : "Location";
  const locationPlaceholder =
    activeTab === "packages" ? "City / Destination" : activeTab === "cabs" ? "Pickup city" : "City / State / Country";
  const checkInLabel = activeTab === "cabs" ? "Travel Date" : "Check In";
  const checkOutLabel = activeTab === "cabs" ? "Return Date" : "Check Out";
  const guestFieldLabel = activeTab === "packages" ? "Travellers" : activeTab === "cabs" ? "Passengers" : "Guests";
  const triggerText = locationInput.trim() || "Search for City, Villa, Location...";
  const openMobileSearch = () => {
    closePanels();
    setIsMobileSearchOpen(true);
  };
  const closeMobileSearch = () => {
    closePanels();
    setIsMobileSearchOpen(false);
  };
  const mobileSearchDialog = isMobileSearchOpen ? (
    <div className="hv-mobile-search-overlay" onMouseDown={closeMobileSearch}>
      <div
        className="hv-mobile-search-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hv-mobile-search-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="hv-mobile-search-header">
          <button
            type="button"
            className="hv-mobile-search-close"
            onClick={closeMobileSearch}
            aria-label="Close search"
          >
            ×
          </button>
          <h2 id="hv-mobile-search-title">Search Villas</h2>
        </header>

        {showTabs ? (
          <div className="hv-mobile-search-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={activeTab === tab.key ? "is-active" : ""}
                onClick={() => {
                  setActiveTab(tab.key);
                  closePanels();
                }}
              >
                {tab.shortLabel || tab.label}
              </button>
            ))}
          </div>
        ) : null}

        <div className="hv-mobile-search-fields">
          <LocationInput
            inputId="hv-location-input-mobile"
            label="City / Villa / Location"
            placeholder="Search for City, Villa, Location..."
            value={locationInput}
            open={openMenu}
            loading={mergedLoading}
            suggestions={mergedSuggestions}
            isPopular={isPopular}
            error={error}
            onChange={handleLocationChange}
            onSelect={handleLocationSelect}
            onFocus={handleLocationFocus}
            onBlur={handleLocationBlur}
          />

          <div className="hv-mobile-date-row">
            <button
              type="button"
              className="hv-mobile-field-button"
              onClick={(event) => {
                setCalendarAnchorRect(event.currentTarget.getBoundingClientRect());
                setOpenCalendar(true);
                setOpenGuests(false);
                setOpenMenu(false);
              }}
            >
              <span>{checkInLabel}</span>
              <strong>{formatFieldDate(checkIn)}</strong>
            </button>
            <span className="hv-mobile-date-arrow" aria-hidden="true">
              →
            </span>
            <button
              type="button"
              className="hv-mobile-field-button"
              onClick={(event) => {
                setCalendarAnchorRect(event.currentTarget.getBoundingClientRect());
                setOpenCalendar(true);
                setOpenGuests(false);
                setOpenMenu(false);
              }}
            >
              <span>{checkOutLabel}</span>
              <strong>{formatFieldDate(checkOut)}</strong>
            </button>
          </div>

          <button
            type="button"
            className="hv-mobile-field-button hv-mobile-guests-button"
            onClick={(event) => {
              setGuestAnchorRect(event.currentTarget.getBoundingClientRect());
              setOpenGuests(true);
              setOpenCalendar(false);
              setOpenMenu(false);
            }}
          >
            <span>{guestFieldLabel}</span>
            <strong>{guestLabel}</strong>
          </button>

          <button
            type="button"
            className="hv-mobile-submit"
            onClick={handleSearch}
            disabled={isSearching}
          >
            {isSearching ? <span className="hv-spinner" aria-hidden="true" /> : null}
            <span>{isSearching ? "Searching" : "Search"}</span>
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <section className={`hv-search-shell ${isCompact ? "is-compact" : ""}`}>
      <button
        type="button"
        className="hv-mobile-search-trigger"
        onClick={openMobileSearch}
        aria-label="Open search"
      >
        <span className="hv-mobile-search-icon" aria-hidden="true">
          Q
        </span>
        <span>{triggerText}</span>
      </button>

      <div className="hv-search-frame hv-desktop-search-frame">
        {showTabs ? (
          <div className="hv-search-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={activeTab === tab.key ? "is-active" : ""}
                onClick={() => {
                  setActiveTab(tab.key);
                  closePanels();
                }}
              >
                <span className="hv-tab-label-full">{tab.label}</span>
                <span className="hv-tab-label-short">{tab.shortLabel || tab.label}</span>
              </button>
            ))}
          </div>
        ) : null}

        <div className={`hv-search-bar ${showTabs ? "" : "is-plain"}`}>
          <LocationInput
            inputId="hv-location-input-desktop"
            label={locationLabel}
            placeholder={locationPlaceholder}
            value={locationInput}
            open={openMenu}
            loading={mergedLoading}
            suggestions={mergedSuggestions}
            isPopular={isPopular}
            error={error}
            onChange={handleLocationChange}
            onSelect={handleLocationSelect}
            onFocus={handleLocationFocus}
            onBlur={handleLocationBlur}
          />

          <div className="hv-field">
            <button
              type="button"
              className="hv-field-button"
              onClick={(event) => {
                setCalendarAnchorRect(event.currentTarget.getBoundingClientRect());
                setOpenCalendar((prev) => !prev);
                setOpenGuests(false);
                setOpenMenu(false);
              }}
            >
              <span className="hv-field-label">{checkInLabel}</span>
              <span>{formatFieldDate(checkIn)}</span>
            </button>
          </div>

          <div className="hv-divider" />

          <div className="hv-field">
            <button
              type="button"
              className="hv-field-button"
              onClick={(event) => {
                setCalendarAnchorRect(event.currentTarget.getBoundingClientRect());
                setOpenCalendar((prev) => !prev);
                setOpenGuests(false);
                setOpenMenu(false);
              }}
            >
              <span className="hv-field-label">{checkOutLabel}</span>
              <span>{formatFieldDate(checkOut)}</span>
            </button>
          </div>

          <div className="hv-divider" />

          <div className="hv-field">
            <button
              type="button"
              className="hv-field-button"
              onClick={(event) => {
                setGuestAnchorRect(event.currentTarget.getBoundingClientRect());
                setOpenGuests((prev) => !prev);
                setOpenCalendar(false);
                setOpenMenu(false);
              }}
            >
              <span className="hv-field-label">{guestFieldLabel}</span>
              <span>{guestLabel}</span>
            </button>
          </div>

          <button
            type="button"
            className="hv-search-btn"
            onClick={handleSearch}
            disabled={isSearching}
          >
            {isSearching ? <span className="hv-spinner" aria-hidden="true" /> : null}
            <span>{isSearching ? "Searching" : "Search"}</span>
          </button>
        </div>
      </div>

      {mobileSearchDialog && typeof document !== "undefined"
        ? createPortal(mobileSearchDialog, document.body)
        : mobileSearchDialog}

      <div className="hv-popover-anchor">
        <DateRangePicker
          open={openCalendar}
          checkIn={checkIn}
          checkOut={checkOut}
          anchorRect={calendarAnchorRect}
          onChange={({ checkIn: nextIn, checkOut: nextOut }) => {
            setCheckIn(nextIn);
            setCheckOut(nextOut);
          }}
          onClose={() => setOpenCalendar(false)}
        />

        <GuestSelector
          open={openGuests}
          anchorRect={guestAnchorRect}
          adults={guests.adults}
          children={guests.children}
          onChange={setGuests}
          onClose={() => setOpenGuests(false)}
        />
      </div>
    </section>
  );
}
